import React, {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {connect} from 'react-redux';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import agent from '../../agent';
import {
  NOTIFICATION_PAGE_LOADED,
  NOTIFICATION_PAGE_UNLOADED,
  NOTIFICATION_SET_PAGE,
} from '../../constants/actionTypes';
import {
  NOTIFICATION_OPERATION_TYPES,
  NOTIFICATION_TYPES
} from '../../constants/notificationConstants';
import {DEFAULT_THEME_COLOR, DEFAULT_GRADIENT, DEFAULT_GRADIENT_HOVER} from "../../constants/theme";
import TopBar from "../TopBar";
import useTranslation from "../../customHooks/translations";
import {getFreakIcon} from "../../utils/freakUtils";
import {formatDateAgo} from "../../utils/dateUtils";
import InfiniteScroll from "react-infinite-scroller";
import CommonLoader from "../Loaders/CommonLoader";
import {useMediaQuery} from "react-responsive";

const getTypeBadgeColor = (type) => {
  switch (type) {
    case NOTIFICATION_TYPES.INVITATION: return { bg: 'rgba(54,201,162,0.15)', color: '#36c9a2' };
    case NOTIFICATION_TYPES.REQUEST_JOIN: return { bg: 'rgba(79,172,254,0.15)', color: '#4facfe' };
    default: return { bg: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)' };
  }
};

const getInvitationTypeName = (notificationType, translation) => {
  if (!notificationType) return 'Error';
  switch (notificationType) {
    case NOTIFICATION_TYPES.INVITATION: return translation.notifications.types.invitation;
    case NOTIFICATION_TYPES.INFO: return translation.notifications.types.info;
    case NOTIFICATION_TYPES.REQUEST_JOIN: return translation.notifications.types.requestJoin;
    // radar, moderation, accept/decline updates… are all informational
    default: return translation.notifications.types.info;
  }
};

const Notification = (props) => {
  const translation = useTranslation();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 992 });

  const [notifications, setNotifications] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = async () => {
    const response = await agent.Notifications.feed(0);
    setNotifications(response.notifications);
  };

  const handleApprove = async (notification) => {
    const op = notification?.operations?.find(o => o.type === NOTIFICATION_OPERATION_TYPES.APPROVE);
    if (op) { await agent.Notifications.approve(op.operation_request); await refresh(); }
  };

  const handleDecline = async (notification) => {
    const op = notification?.operations?.find(o => o.type === NOTIFICATION_OPERATION_TYPES.DECLINE);
    if (op) { await agent.Notifications.decline(op.operation_request); await agent.Notifications.delete(notification.id); await refresh(); }
  };

  const handleDelete = async (notification) => {
    await agent.Notifications.delete(notification.id);
    await refresh();
  };

  const handleRead = async (notification) => {
    await agent.Notifications.read(notification.id);
    await refresh();
  };

  const handleOpen = async (notification) => {
    if (notification?.freakPlace?.map) {
      navigate(notification.link, { state: { freakPlaceMapSlug: notification.freakPlace?.map?.slug, freakPlaceMap: notification?.freakPlace?.map } });
    } else {
      navigate(notification.link);
    }
    await agent.Notifications.read(notification.id);
    await refresh();
  };

  const loadMore = async (page) => {
    if (!hasMore) return;
    const results = await agent.Notifications.feed(page - 1);
    setNotifications(prev => [...prev, ...results.notifications]);
    setIsLoading(false);
    if (results.notifications.length === 0) setHasMore(false);
  };

  return (
    <>
      <TopBar/>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #0d1b2a 0%, #091420 100%)',
          pt: 1,
          pb: 10,
          pl: isDesktop && props.leftNavigationOpen ? '296px' : 0,
        }}
      >
        <Box sx={{ maxWidth: 600, mx: 'auto', px: { xs: 1, sm: 2 } }}>
          <Typography
            variant="h6"
            sx={{
              background: DEFAULT_GRADIENT,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontWeight: 700,
              mb: 2,
              mt: 1,
            }}
          >
            {translation.notifications.title || 'Notifications'}
          </Typography>

          <InfiniteScroll pageStart={0} loadMore={loadMore} hasMore={hasMore} loader={<CommonLoader key="loader"/>}>
            {(notifications && notifications.length) || isLoading
              ? (
                <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {notifications.map((notification, idx) => {
                    const isPending = notification.status === 'pending';
                    const isActionable = notification.type === NOTIFICATION_TYPES.INVITATION || notification.type === NOTIFICATION_TYPES.REQUEST_JOIN;
                    const badge = getTypeBadgeColor(notification.type);

                    return (
                      <Box
                        key={notification.id || idx}
                        onClick={() => handleRead(notification)}
                        sx={{
                          background: 'linear-gradient(135deg, #0d1b2a 0%, #0d2a1e 100%)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          borderLeft: notification.read
                            ? '3px solid rgba(255,255,255,0.08)'
                            : `3px solid transparent`,
                          borderImage: notification.read
                            ? undefined
                            : `${DEFAULT_GRADIENT} 1`,
                          borderRadius: '12px',
                          overflow: 'hidden',
                          transition: 'box-shadow 0.2s',
                          '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.4)', cursor: 'pointer' },
                        }}
                      >
                        <ListItem
                          alignItems="flex-start"
                          sx={{ px: 2, pt: 1.5, pb: 0.5 }}
                          secondaryAction={
                            (notification?.freak?.context || notification.freakHub?.context || notification.freakPlace?.context)
                              ? <IconButton size="small" sx={{ color: DEFAULT_THEME_COLOR, '&:focus': { outline: 'none' } }}>
                                  {getFreakIcon(notification.freak?.context || notification.freakHub?.context || notification.freakPlace?.context)}
                                </IconButton>
                              : null
                          }
                        >
                          <ListItemAvatar>
                            <Link to={`/@${notification.author.username}`} onClick={e => e.stopPropagation()}>
                              <Avatar
                                alt={notification.user.username}
                                src={notification.author.imageUrl}
                                sx={{ width: 44, height: 44, border: '2px solid rgba(255,255,255,0.1)' }}
                              />
                            </Link>
                          </ListItemAvatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                              <Chip
                                label={getInvitationTypeName(notification.type, translation)}
                                size="small"
                                sx={{
                                  backgroundColor: badge.bg,
                                  color: badge.color,
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  height: 20,
                                }}
                              />
                              {!notification.read && (
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: DEFAULT_GRADIENT, flexShrink: 0 }} />
                              )}
                            </Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 }}>
                              {notification.name}
                            </Typography>
                          </Box>
                        </ListItem>

                        {/* Action row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pb: 1.5, flexWrap: 'wrap' }}>
                          {isActionable && isPending && (
                            <>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<CheckIcon/>}
                                sx={{ background: DEFAULT_GRADIENT, color: 'white', fontWeight: 700, zIndex: 0, borderRadius: 2, fontSize: '0.75rem', '&:hover': { background: DEFAULT_GRADIENT_HOVER }, '&:focus': { outline: 'none' } }}
                                onClick={(e) => { e.stopPropagation(); handleApprove(notification); }}
                              >
                                {translation.notifications.accept}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<CloseIcon/>}
                                sx={{ color: '#f44336', borderColor: 'rgba(244,67,54,0.5)', zIndex: 0, borderRadius: 2, fontSize: '0.75rem', '&:hover': { borderColor: '#f44336', backgroundColor: 'rgba(244,67,54,0.08)' }, '&:focus': { outline: 'none' } }}
                                onClick={(e) => { e.stopPropagation(); handleDecline(notification); }}
                              >
                                {translation.notifications.decline}
                              </Button>
                            </>
                          )}

                          {!isPending && (
                            <Button
                              size="small"
                              variant="outlined"
                              endIcon={<SendIcon/>}
                              sx={{ color: DEFAULT_THEME_COLOR, borderColor: DEFAULT_THEME_COLOR, zIndex: 0, borderRadius: 2, fontSize: '0.75rem', '&:hover': { backgroundColor: 'rgba(54,201,162,0.08)' }, '&:focus': { outline: 'none' } }}
                              onClick={(e) => { e.stopPropagation(); handleOpen(notification); }}
                            >
                              {translation.notifications.show}
                            </Button>
                          )}

                          <IconButton
                            size="small"
                            sx={{ color: 'rgba(255,255,255,0.3)', ml: 'auto', '&:hover': { color: '#f44336' }, '&:focus': { outline: 'none' } }}
                            onClick={(e) => { e.stopPropagation(); handleDelete(notification); }}
                          >
                            <DeleteIcon fontSize="small"/>
                          </IconButton>

                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.72rem' }}>
                            {formatDateAgo(notification.createdAt)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </List>
              )
              : (
                <Box display="flex" alignItems="center" justifyContent="center" height="60vh">
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    {translation.notifications.emptyList}
                  </Typography>
                </Box>
              )
            }
          </InfiniteScroll>
        </Box>
      </Box>
    </>
  );
};

const mapStateToProps = state => ({
  ...state.notifications,
  currentUser: state.common.currentUser,
  context: state.common.context,
  leftNavigationOpen: state.common.leftNavigationOpen,
});

const mapDispatchToProps = dispatch => ({
  onLoad: payload => dispatch({type: NOTIFICATION_PAGE_LOADED, payload}),
  onUnload: () => dispatch({type: NOTIFICATION_PAGE_UNLOADED}),
  onSetPage: (page, payload) => dispatch({type: NOTIFICATION_SET_PAGE, page, payload}),
});

export default connect(mapStateToProps, mapDispatchToProps)(Notification);
